import {
  claimPuzzleAchievement,
  getReachedPuzzleMilestone,
  getReachedPuzzleMedal,
} from "./puzzleAchievements";
import Head from "next/head";
import Link from "next/link";
import { useCallback, useEffect, useRef, useState } from "react";
import { useSettings } from "@/redux/settings";
import { useUser } from "@/redux/user";
import { useSignIn } from "@/redux/signIn";
import { api } from "@/services/api";
import { Button } from "@/components/ui/button";
import { LEAGUE_NAME_PLACEMENT } from "@chesshotel/constants";
import PuzzleBoard, {
  PuzzleRatingUpdate,
  PuzzleSessionAction,
  PuzzleSessionReply,
} from "./PuzzleBoard";
import { clearGuestToken, guestToken } from "./guestSession";
import {
  createEmptyPuzzleSolvedProgress,
  getPuzzleLeagueMedalImageSrc,
  getPuzzleLeagueTier,
  getPuzzleSolvedProgressStorageKey,
  PUZZLE_PLACEMENT_TARGET,
  PuzzleLeagueTier,
  readPuzzleSolvedProgress,
  recordPuzzleSolved,
} from "./puzzleMedals";
import {
  PuzzleDifficultyKey,
  readPuzzleDifficulty,
  writePuzzleDifficulty,
} from "./puzzleDifficulty";
import useTranslation from "next-translate/useTranslation";

const MAX_PLAYED_PUZZLES = 300;
const STARTING_PUZZLE_RATING = 800;
const PUZZLE_PLACEMENT_SOLVES = PUZZLE_PLACEMENT_TARGET;

export type PuzzlePayload = {
  season?: number | null;
  puzzleId?: string;
  fen: string;
  lastMove?: string;
  rating?: number;
  userElo?: number;
  gameNr?: number;
  leagueName?: string;
  divisionId?: number;
  solvedCount?: number;
  points?: number;
  cleanSolveStreak?: number;
  promotionProgress?: number;
  rank?: number | null;
  nextRank?: number | null;
  nextRankPoints?: number | null;
  hearts?: number;
  hintAvailable?: boolean;
};

function getPlayedPuzzleStorageKey(username?: string) {
  return username ? `played_puzzles_${username}` : "played_puzzles_guest";
}

function readPlayedPuzzleIds(storageKey: string) {
  try {
    const storedPlayed = localStorage.getItem(storageKey);
    const parsed = storedPlayed ? JSON.parse(storedPlayed) : [];
    return Array.isArray(parsed)
      ? parsed.filter((id): id is string => typeof id === "string")
      : [];
  } catch (e) {
    console.error("Error reading played puzzles", e);
    return [];
  }
}

function rememberPlayedPuzzle(storageKey: string, puzzleId: string) {
  try {
    const played = readPlayedPuzzleIds(storageKey);
    if (!played.includes(puzzleId)) {
      played.push(puzzleId);
    }
    const recentPlayed = played.slice(-MAX_PLAYED_PUZZLES);
    localStorage.setItem(storageKey, JSON.stringify(recentPlayed));
    return recentPlayed;
  } catch (e) {
    console.error("Error saving played puzzle", e);
    return [];
  }
}

function normalizePuzzleRating(value: unknown) {
  const rating = Number(value);
  return Number.isFinite(rating) && rating > 0
    ? Math.round(rating)
    : STARTING_PUZZLE_RATING;
}

function normalizePuzzleAttempts(value: unknown) {
  const attempts = Number(value);
  return Number.isFinite(attempts) && attempts >= 0 ? Math.floor(attempts) : 0;
}

function isPuzzleRatingRevealed({ attempts }: { attempts: number }) {
  return attempts >= PUZZLE_PLACEMENT_SOLVES;
}

function PuzzleChallengeMessage({
  title,
  text,
}: {
  title: string;
  text: string;
}) {
  const settings = useSettings();
  const boardSize = settings.boardSize || 504;
  const panelHeight = settings.isDesktop ? boardSize + 44 + "px" : undefined;

  return (
    <div className="flex flex-col items-center md:mt-4">
      <div className="flex w-full flex-col items-center md:w-auto md:flex-row md:items-start md:rounded-lg">
        <div className="flex justify-center md:mr-4">
          <div
            className="flex items-center justify-center rounded-md border border-athens-gray-150 bg-white text-sm text-gray-500"
            style={{ width: boardSize, height: boardSize }}
          >
            {title}
          </div>
        </div>
        <div
          className="mb-20 flex h-full w-full flex-col justify-center rounded-md bg-athens-gray-150 px-5 text-sm md:mb-0 md:w-[400px] lg:w-[400px]"
          style={{ height: panelHeight }}
        >
          <div className="text-lg font-bold text-gray-800">{title}</div>
          <div className="mt-2 text-muted-foreground">{text}</div>
        </div>
      </div>
    </div>
  );
}

function PuzzleLeagueUnlockedPopup({
  tier,
  milestone,
  guest,
  onClose,
}: {
  tier: PuzzleLeagueTier;
  milestone?: number | null;
  guest: boolean;
  onClose: () => void;
}) {
  const { t } = useTranslation("puzzles");
  const leagueLabel = t(`league.tiers.${tier.label.toLowerCase()}`);
  useEffect(() => {
    const onKeyDown = (event: KeyboardEvent) => {
      if (event.key === "Escape") onClose();
    };
    window.addEventListener("keydown", onKeyDown);
    return () => window.removeEventListener("keydown", onKeyDown);
  }, [onClose]);
  return (
    <div
      role="dialog"
      aria-modal="true"
      aria-labelledby="puzzle-promotion-title"
      className="fixed inset-0 z-[1000] flex items-center justify-center px-4"
    >
      <div className="absolute inset-0 bg-gray-950/45" onClick={onClose} />
      <div className="relative flex w-full max-w-[420px] flex-col items-center rounded-lg bg-white px-6 pb-6 pt-7 text-center shadow-2xl">
        <button
          className="absolute right-4 top-3 text-2xl leading-none text-gray-400 hover:text-gray-700"
          onClick={onClose}
          aria-label={t("league.closeCelebration")}
          type="button"
        >
          ×
        </button>
        <img
          src={getPuzzleLeagueMedalImageSrc(tier)}
          alt={t("league.viewStandingsLabel", { league: leagueLabel })}
          className="h-24 w-24 object-contain drop-shadow-md"
        />
        <div
          id="puzzle-promotion-title"
          className="mt-4 text-xl font-bold text-gray-900"
        >
          {t("league.welcomeLeague", { league: leagueLabel })}
        </div>
        <div className="mt-2 text-sm text-muted-foreground">
          {t("league.enteredLeague", { league: leagueLabel })}
        </div>
        {milestone != null && (
          <div className="mt-4 rounded-md bg-slate-100 px-4 py-3 text-sm font-semibold text-altblue-700">
            {t("league.alsoPointsReached", {
              points: milestone.toLocaleString(),
            })}
          </div>
        )}
        {guest ? (
          <>
            <p className="mt-4 text-sm text-slate-600">
              {t("league.createAccountLong")}
            </p>
            <Button asChild autoFocus className="mt-5 w-full" size="lg">
              <Link href="/register">{t("mistakes.createAccount")}</Link>
            </Button>
            <Button className="mt-2 w-full" variant="quiet" onClick={onClose}>
              {t("league.continueSolving")}
            </Button>
          </>
        ) : (
          <Button autoFocus className="mt-6 w-full" size="lg" onClick={onClose}>
            {t("league.continueSolving")}
          </Button>
        )}
      </div>
    </div>
  );
}

type PuzzleChallengeProps = {
  initialPuzzle?: PuzzlePayload | null;
};

export default function PuzzleChallenge({
  initialPuzzle = null,
}: PuzzleChallengeProps) {
  const { t } = useTranslation("puzzles");
  const user = useUser();
  const auth = useSignIn();
  const username = user?.memberLoggedIn ? user.username : undefined;
  const [currentPuzzle, setCurrentPuzzle] = useState<PuzzlePayload | null>(
    initialPuzzle,
  );
  const [nextLoading, setNextLoading] = useState(false);
  const [preferencesReady, setPreferencesReady] = useState(false);
  const [loadError, setLoadError] = useState(false);
  const [retryCount, setRetryCount] = useState(0);
  const [solvedProgress, setSolvedProgress] = useState(
    createEmptyPuzzleSolvedProgress,
  );
  const [promotionProgress, setPromotionProgress] = useState(0);
  const [unlockedLeague, setUnlockedLeague] = useState<PuzzleLeagueTier | null>(
    null,
  );
  const [medalReached, setMedalReached] = useState<number | null>(null);
  const [season, setSeason] = useState<number | null>(
    initialPuzzle?.season ?? null,
  );
  const [milestoneReached, setMilestoneReached] = useState<number | null>(null);
  const [promotionMilestone, setPromotionMilestone] = useState<number | null>(
    null,
  );
  const [puzzleDifficulty, setPuzzleDifficulty] =
    useState<PuzzleDifficultyKey>("balanced");
  const [puzzleRating, setPuzzleRating] = useState(
    normalizePuzzleRating(initialPuzzle?.userElo),
  );
  const [ratingGameNr, setRatingGameNr] = useState(
    normalizePuzzleAttempts(initialPuzzle?.gameNr),
  );
  const [ratingLeagueName, setRatingLeagueName] = useState<string | undefined>(
    initialPuzzle?.leagueName,
  );
  const [puzzlePoints, setPuzzlePoints] = useState(
    normalizePuzzleAttempts(initialPuzzle?.points),
  );
  const [lastPointsEarned, setLastPointsEarned] = useState<number | null>(null);
  const [lastStreakBonus, setLastStreakBonus] = useState(0);
  const [rank, setRank] = useState<number | null>(initialPuzzle?.rank ?? null);
  const [nextRank, setNextRank] = useState<number | null | undefined>(
    initialPuzzle?.nextRank ?? null,
  );
  const [nextRankPoints, setNextRankPoints] = useState<
    number | null | undefined
  >(initialPuzzle?.nextRankPoints ?? null);
  const guestTokenRef = useRef("");
  const usernameRef = useRef<string | undefined>(undefined);

  const applyFetchedPuzzleRating = useCallback((puzzle: PuzzlePayload) => {
    if (puzzle.season !== undefined) setSeason(puzzle.season);
    if (puzzle.userElo !== undefined) {
      setPuzzleRating(normalizePuzzleRating(puzzle.userElo));
    }
    if (puzzle.gameNr !== undefined) {
      setRatingGameNr(normalizePuzzleAttempts(puzzle.gameNr));
    }
    if (puzzle.leagueName !== undefined) {
      setRatingLeagueName(puzzle.leagueName);
    }
    if (puzzle.solvedCount !== undefined) {
      const solvedCount = normalizePuzzleAttempts(puzzle.solvedCount);
      setSolvedProgress((currentProgress) => ({
        ...currentProgress,
        solved: Math.max(currentProgress.solved, solvedCount),
      }));
    }
    if (puzzle.points !== undefined) {
      setPuzzlePoints(normalizePuzzleAttempts(puzzle.points));
    }
    if (puzzle.promotionProgress !== undefined) {
      setPromotionProgress(normalizePuzzleAttempts(puzzle.promotionProgress));
    }
    if (puzzle.rank !== undefined) {
      setRank(puzzle.rank);
    }
    if (puzzle.nextRank !== undefined) {
      setNextRank(puzzle.nextRank);
    }
    if (puzzle.nextRankPoints !== undefined) {
      setNextRankPoints(puzzle.nextRankPoints);
    }
  }, []);

  const requestPuzzle = useCallback(async () => {
    if (!guestTokenRef.current) guestTokenRef.current = guestToken();
    const start = () =>
      api.post(
        "/puzzle-league/start",
        { difficulty: puzzleDifficulty },
        {
          timeout: 15000,
          headers: { "X-Puzzle-Guest": guestTokenRef.current },
        },
      );
    const response = await start().catch((error) => {
      if (user.memberLoggedIn || error.response?.status !== 401) throw error;
      clearGuestToken();
      guestTokenRef.current = guestToken();
      return start();
    });
    const { puzzle, progress, hearts, hintAvailable } = response.data;
    if (user.memberLoggedIn) clearGuestToken();

    return {
      puzzleId: puzzle.id,
      fen: puzzle.fen,
      lastMove: puzzle.lastMove,
      rating: puzzle.rating,
      season: progress.season,
      userElo: progress.rating,
      gameNr: progress.attempts,
      leagueName: progress.division,
      divisionId: progress.divisionId,
      solvedCount: progress.solved,
      points: progress.points,
      cleanSolveStreak: progress.streak,
      promotionProgress: progress.promotionProgress,
      hearts,
      hintAvailable,
    } as PuzzlePayload;
  }, [puzzleDifficulty, user.memberLoggedIn]);

  const refreshStandings = useCallback(async () => {
    if (!user.memberLoggedIn) return;
    try {
      const response = await api.get("/puzzle-league/standings", {
        timeout: 15000,
      });
      setRank(response.data.rank ?? null);
      setNextRank(response.data.nextRank ?? null);
      setNextRankPoints(response.data.nextRankPoints ?? null);
    } catch (error) {
      console.error("Error fetching puzzle standings", error);
    }
  }, [user.memberLoggedIn]);

  useEffect(() => {
    const previousUsername = usernameRef.current;
    usernameRef.current = username;
    if (previousUsername !== username) setCurrentPuzzle(null);
    setSolvedProgress(
      readPuzzleSolvedProgress(getPuzzleSolvedProgressStorageKey(username)),
    );

    if (previousUsername === username) return;

    setPuzzleRating(STARTING_PUZZLE_RATING);
    setRatingGameNr(0);
    setRatingLeagueName(LEAGUE_NAME_PLACEMENT);
    setPuzzlePoints(0);
    setLastPointsEarned(null);
    setLastStreakBonus(0);
    setPromotionProgress(0);
    setRank(null);
    setNextRank(null);
    setNextRankPoints(null);
  }, [username]);

  useEffect(() => {
    setPuzzleDifficulty(readPuzzleDifficulty());
    setPreferencesReady(true);
  }, []);

  useEffect(() => {
    let cancelled = false;

    async function preparePuzzle() {
      if (!preferencesReady || auth.loading || currentPuzzle) return;

      try {
        setLoadError(false);
        const puzzle = await requestPuzzle();
        if (cancelled) return;
        applyFetchedPuzzleRating(puzzle);
        setCurrentPuzzle(puzzle);
      } catch (e) {
        if (cancelled) return;
        console.error("Error fetching puzzle", e);
        setLoadError(true);
        setCurrentPuzzle(null);
      }
    }

    void preparePuzzle();

    return () => {
      cancelled = true;
    };
  }, [
    applyFetchedPuzzleRating,
    auth.loading,
    currentPuzzle,
    preferencesReady,
    requestPuzzle,
    retryCount,
  ]);

  useEffect(() => {
    if (currentPuzzle?.puzzleId) void refreshStandings();
  }, [currentPuzzle?.puzzleId, refreshStandings]);

  const handleNext = async () => {
    if (nextLoading) return;
    setMilestoneReached(null);
    setMedalReached(null);

    setNextLoading(true);
    try {
      const puzzle = await requestPuzzle();
      applyFetchedPuzzleRating(puzzle);
      setCurrentPuzzle(puzzle);
    } catch (e) {
      console.error("Error fetching puzzle", e);
    } finally {
      setNextLoading(false);
    }
  };

  const handleComplete = () => {
    if (!currentPuzzle?.puzzleId) return;

    const storageKey = getPlayedPuzzleStorageKey(username);
    rememberPlayedPuzzle(storageKey, currentPuzzle.puzzleId);
    const nextSolvedProgress = recordPuzzleSolved(
      getPuzzleSolvedProgressStorageKey(username),
      currentPuzzle.puzzleId,
    );
    setSolvedProgress(nextSolvedProgress);
  };

  const handleDifficultyChange = (difficulty: PuzzleDifficultyKey) => {
    setPuzzleDifficulty(difficulty);
    writePuzzleDifficulty(difficulty);
  };

  const ratingRevealed = isPuzzleRatingRevealed({
    attempts: ratingGameNr,
  });

  const handleRatingChange = (ratingUpdate: PuzzleRatingUpdate) => {
    const nextRating = normalizePuzzleRating(ratingUpdate.newElo);
    if (ratingUpdate.season !== undefined) setSeason(ratingUpdate.season);
    const nextGameNr =
      ratingUpdate.gameNr !== undefined
        ? normalizePuzzleAttempts(ratingUpdate.gameNr)
        : ratingGameNr;
    const nextLeagueName = ratingUpdate.leagueName ?? ratingLeagueName;
    const puzzleAlreadySolved = currentPuzzle?.puzzleId
      ? solvedProgress.completedPuzzleIds.includes(currentPuzzle.puzzleId)
      : false;
    const predictedSolvedCount =
      ratingUpdate.won && !puzzleAlreadySolved
        ? solvedProgress.solved + 1
        : solvedProgress.solved;
    const nextSolvedCount =
      ratingUpdate.solvedCount !== undefined
        ? Math.max(
            predictedSolvedCount,
            normalizePuzzleAttempts(ratingUpdate.solvedCount),
          )
        : predictedSolvedCount;

    setPuzzleRating(nextRating);

    if (ratingUpdate.points !== undefined) {
      const nextPoints = normalizePuzzleAttempts(ratingUpdate.points);
      setPuzzlePoints(nextPoints);
    }
    if (ratingUpdate.pointsEarned !== undefined) {
      setLastPointsEarned(normalizePuzzleAttempts(ratingUpdate.pointsEarned));
    }
    if (ratingUpdate.streakBonus !== undefined) {
      setLastStreakBonus(normalizePuzzleAttempts(ratingUpdate.streakBonus));
    }
    if (ratingUpdate.promotionProgress !== undefined) {
      setPromotionProgress(
        normalizePuzzleAttempts(ratingUpdate.promotionProgress),
      );
    }
    if (ratingUpdate.rank !== undefined) {
      setRank(ratingUpdate.rank);
    }
    if (ratingUpdate.nextRank !== undefined) {
      setNextRank(ratingUpdate.nextRank);
    }
    if (ratingUpdate.nextRankPoints !== undefined) {
      setNextRankPoints(ratingUpdate.nextRankPoints);
    }

    setRatingGameNr(nextGameNr);
    setRatingLeagueName(nextLeagueName);
    if (ratingUpdate.solvedCount !== undefined) {
      setSolvedProgress((currentProgress) => ({
        ...currentProgress,
        solved: Math.max(currentProgress.solved, nextSolvedCount),
      }));
    }

    const nextRatingRevealed = isPuzzleRatingRevealed({
      attempts: nextGameNr,
    });

    const previousTier = getPuzzleLeagueTier({
      rating: puzzleRating,
      leagueName: ratingLeagueName,
    });
    const nextTier = getPuzzleLeagueTier({
      rating: nextRating,
      leagueName: nextLeagueName,
    });
    const owner = username || "guest";
    const reached = getReachedPuzzleMilestone(
      puzzlePoints,
      ratingUpdate.points ?? puzzlePoints,
    );
    const milestone =
      nextRatingRevealed &&
      reached !== null &&
      claimPuzzleAchievement(owner, `points:${reached}`)
        ? reached
        : null;
    const promoted =
      nextRatingRevealed &&
      (!ratingRevealed || nextTier.minRating > previousTier.minRating) &&
      claimPuzzleAchievement(owner, `league:${nextTier.leagueName}`);

    const achievementSeason = ratingUpdate.season ?? season;
    const reachedMedal = getReachedPuzzleMedal(rank, ratingUpdate.rank);
    const newMedal =
      ratingRevealed &&
      nextRatingRevealed &&
      achievementSeason !== null &&
      (season === null || season === achievementSeason) &&
      previousTier.leagueName === nextTier.leagueName &&
      reachedMedal !== null &&
      claimPuzzleAchievement(
        owner,
        `season:${achievementSeason}:league:${nextTier.leagueName}:medal:${reachedMedal}`,
      )
        ? reachedMedal
        : null;

    if (promoted) {
      setMedalReached(null);
      setMilestoneReached(null);
      setPromotionMilestone(milestone);
      setUnlockedLeague(nextTier);
    } else {
      setMedalReached(newMedal);
      if (milestone !== null) setMilestoneReached(milestone);
    }
  };

  const handleSessionAction = async (
    action: PuzzleSessionAction,
  ): Promise<PuzzleSessionReply> => {
    if (!currentPuzzle?.puzzleId) {
      throw new Error("No active puzzle session");
    }

    const response = await api.post(
      `/puzzle-league/${currentPuzzle.puzzleId}/action`,
      action,
      {
        timeout: 15000,
        headers: { "X-Puzzle-Guest": guestTokenRef.current },
      },
    );
    const data = response.data;
    const progress = data.progress;
    handleRatingChange({
      season: progress.season,
      newElo: progress.rating,
      eloChange: data.eloChange || 0,
      won: Boolean(data.done),
      gameNr: progress.attempts,
      leagueName: progress.division,
      divisionId: progress.divisionId,
      solvedCount: progress.solved,
      points: progress.points,
      pointsEarned: data.pointsEarned,
      streakBonus: data.streakBonus,
      cleanSolveStreak: progress.streak,
      promotionProgress: progress.promotionProgress,
    });
    if (data.ratingSettled) void refreshStandings();
    return data as PuzzleSessionReply;
  };

  return (
    <>
      <Head>
        <title>{t("league.metaTitle")}</title>
        <meta name="description" content={t("league.metaDescription")} />
      </Head>
      <main className="min-h-[calc(100vh-80px)] px-0 pb-6 md:px-4 md:pb-20">
        {currentPuzzle ? (
          <PuzzleBoard
            key={currentPuzzle.puzzleId || currentPuzzle.fen}
            medalReached={medalReached}
            milestoneReached={milestoneReached}
            fen={currentPuzzle.fen}
            initialLastMove={currentPuzzle.lastMove}
            onNext={handleNext}
            onComplete={handleComplete}
            onSessionAction={handleSessionAction}
            initialLives={currentPuzzle.hearts}
            initialHintAvailable={currentPuzzle.hintAvailable}
            puzzleRating={currentPuzzle.rating}
            userElo={puzzleRating}
            gameNr={ratingGameNr}
            leagueName={ratingLeagueName}
            solvedProgress={solvedProgress}
            puzzlePoints={puzzlePoints}
            lastPointsEarned={lastPointsEarned}
            lastStreakBonus={lastStreakBonus}
            rank={rank}
            nextRank={nextRank}
            nextRankPoints={nextRankPoints}
            promotionProgress={promotionProgress}
            nextLoading={nextLoading}
            puzzleDifficulty={puzzleDifficulty}
            ratingRevealed={ratingRevealed}
            onPuzzleDifficultyChange={handleDifficultyChange}
          />
        ) : (
          <div className="px-4 py-12 text-center" role="status">
            <p>{t(loadError ? "league.loadFailed" : "league.loading")}</p>
            {loadError && (
              <Button
                className="mt-4"
                onClick={() => setRetryCount((count) => count + 1)}
              >
                {t("general.tryAgain")}
              </Button>
            )}
          </div>
        )}
      </main>
      {unlockedLeague && (
        <PuzzleLeagueUnlockedPopup
          tier={unlockedLeague}
          milestone={promotionMilestone}
          guest={!user?.memberLoggedIn}
          onClose={() => setUnlockedLeague(null)}
        />
      )}
    </>
  );
}
